// src/pages/AboutPage.tsx
import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import PageHeader from '@/components/PageHeader';
import { cn } from '@/lib/utils';
import CtaGradient from '@/components/CtaGradient';
import { OptimizedImage } from '@/components/ui/OptimizedImage';

const AboutPage = () => {
  const whyHoptiraRef = useRef<HTMLDivElement>(null);
  const isWhyHoptiraInView = useInView(whyHoptiraRef, {
    once: true,
    amount: 0.2,
  });

  return (
    <div>
      {/* PageHeader for the top section */}
      <PageHeader title="We simplify complex challenges" />

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}>
              <p className="text-2xl md:text-3xl text-gray-700 mb-10 leading-relaxed">
                We're a passionate team of digital creators, technologists, and
                problem solvers dedicated to simplifying complex challenges
                through innovative solutions.
              </p>

              <p className="text-2xl md:text-3xl text-gray-700 leading-relaxed">
                From startups to enterprises, we partner with businesses to
                transform their unique challenges into elegant digital solutions
                that drive growth and enhance user experience.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Hoptira Section */}
      <section ref={whyHoptiraRef} className="pt-24 bg-white relative">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={
              isWhyHoptiraInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.6 }}
            className="mb-16">
            <h2 className="text-5xl md:text-6xl font-bold">Why Hoptira</h2>
            <motion.div
              className="h-1 w-24 bg-secondary mt-6"
              initial={{ width: 0 }}
              animate={isWhyHoptiraInView ? { width: 96 } : { width: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            />
          </motion.div>

          {/* Reason 1 */}
          <WhyHoptiraItem
            number="01"
            title="Founder-Led Expertise"
            description="Unlike large agencies where work is often delegated to junior talent, Hoptira provides direct access to our experienced founders throughout your project. Our senior-level teams bring cross-disciplinary expertise to every challenge, ensuring exceptional results and personalized attention."
            imageSrc="/about-us-why-hoptira-1.svg"
            isInView={isWhyHoptiraInView}
            delay={0.1}
          />

          {/* Reason 2 */}
          <WhyHoptiraItem
            number="02"
            title="True Partnership Approach"
            description="Communication and transparency form the foundation of our client relationships. We've developed an agile mindset from our work with innovative startups while applying the structured project management principles we've refined through enterprise engagements. This balanced approach delivers a premium client experience from concept to completion."
            imageSrc="/about-us-why-hoptira-2.svg"
            isInView={isWhyHoptiraInView}
            delay={0.3}
            reverse
          />

          {/* Reason 3 */}
          <WhyHoptiraItem
            number="03"
            title="Future-Proof Solutions"
            description="We design with longevity in mind, creating scalable solutions that grow with your business. Our portfolio showcases websites and digital products that have remained effective for 5+ years without significant updates - proof that our work stands the test of time and continues delivering value long after launch."
            imageSrc="/about-us-why-hoptira-3.svg"
            isInView={isWhyHoptiraInView}
            delay={0.5}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="pt-24 pb-48 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <CtaGradient />
          </div>
        </div>
      </section>
    </div>
  );
};

interface WhyHoptiraItemProps {
  number: string;
  title: string;
  description: string;
  imageSrc: string;
  isInView: boolean;
  delay: number;
  reverse?: boolean;
}

const WhyHoptiraItem = ({
  number,
  title,
  description,
  imageSrc,
  isInView,
  delay,
  reverse = false,
}: WhyHoptiraItemProps) => {
  return (
    <motion.div
      className={cn(
        'grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16 mb-20 md:mb-32 items-center',
        reverse ? 'md:grid-flow-col' : '',
      )}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay }}>
      {/* Content column */}
      <div className={cn(reverse ? 'md:order-2' : '')}>
        <div className="text-4xl md:text-7xl font-light text-gray-300 mb-3">
          {number}
        </div>
        <h3 className="text-3xl md:text-5xl font-bold mb-6">{title}</h3>
        <p className="text-lg md:text-2xl text-gray-700 leading-relaxed">
          {description}
        </p>
      </div>

      {/* Image column */}
      <div className={cn('flex justify-center', reverse ? 'md:order-1' : '')}>
        <OptimizedImage
          src={imageSrc}
          alt={title}
          className="w-full max-w-md"
          withAnimation={true}
          animationProps={{
            initial: { opacity: 0, scale: 0.9 },
            animate: isInView
              ? { opacity: 1, scale: 1 }
              : { opacity: 0, scale: 0.9 },
            transition: { duration: 0.7, delay: delay + 0.2 },
          }}
        />
      </div>
    </motion.div>
  );
};

export default AboutPage;
